import { GOOGLE_OAUTH_PATH } from '@proton/shared/lib/api/activation';
import { createUrl } from '@proton/shared/lib/fetch/helpers';
import type { ApiEnvironmentConfig } from '@proton/shared/lib/interfaces';

import { EASY_SWITCH_FEATURES, ImportProvider, ImportType, OAUTH_PROVIDER } from '../interface';
import { G_OAUTH_REDIRECT_PATH, M_OAUTH_REDIRECT_PATH, O_OAUTH_REDIRECT_PATH, Z_OAUTH_REDIRECT_PATH } from '../path';

export const getEasySwitchFeaturesFromProducts = (importTypes: ImportType[]) => {
    const features: EASY_SWITCH_FEATURES[] = [];
    importTypes.forEach((importType) => {
        switch (importType) {
            case ImportType.MAIL:
                features.push(EASY_SWITCH_FEATURES.IMPORT_MAIL);
                break;
            case ImportType.CALENDAR:
                features.push(EASY_SWITCH_FEATURES.IMPORT_CALENDAR);
                break;
            case ImportType.CONTACTS:
                features.push(EASY_SWITCH_FEATURES.IMPORT_CONTACTS);
                break;
            case ImportType.DRIVE:
                features.push(EASY_SWITCH_FEATURES.IMPORT_DRIVE);
                break;
            default:
                break;
        }
    });

    return features;
};

export const generateGoogleOAuthParams = ({
    redirectUri,
    loginHint,
    features,
}: {
    redirectUri: string;
    loginHint?: string;
    features: EASY_SWITCH_FEATURES[];
}) => ({
    proton_feature: features,
    redirect_uri: redirectUri,
    loginHint: loginHint ? loginHint : undefined,
});

export const generateGoogleOAuthUrl = ({
    redirectUri,
    loginHint,
    features,
}: {
    redirectUri: string;
    features: EASY_SWITCH_FEATURES[];
    loginHint?: string;
}) => {
    return createUrl(
        GOOGLE_OAUTH_PATH,
        generateGoogleOAuthParams({ redirectUri, loginHint, features }),
        window.location.origin
    ).toString();
};

const generateOutlookOAuthUrl = (params: URLSearchParams, config: ApiEnvironmentConfig) => {
    params.append('client_id', config['oauth.outlook.client_id']);
    return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params.toString()}`;
};

const generateZoomOAuthUrl = (params: URLSearchParams, config: ApiEnvironmentConfig) => {
    params.append('client_id', config['oauth.zoom.client_id']);
    return `https://zoom.us/oauth/authorize?${params.toString()}`;
};

export const getOAuthRedirectURL = (provider: ImportProvider | OAUTH_PROVIDER) => {
    const { protocol, host } = window.location;

    const path = (() => {
        switch (provider) {
            case ImportProvider.GOOGLE:
            case OAUTH_PROVIDER.GOOGLE:
            case OAUTH_PROVIDER.GSUITE:
                return G_OAUTH_REDIRECT_PATH;
            case ImportProvider.OUTLOOK:
            case OAUTH_PROVIDER.OUTLOOK:
                return O_OAUTH_REDIRECT_PATH;
            case OAUTH_PROVIDER.MICROSOFT_BUSINESS:
                return M_OAUTH_REDIRECT_PATH;
            case OAUTH_PROVIDER.ZOOM:
                return Z_OAUTH_REDIRECT_PATH;
            default:
                throw new Error('Provider does not exist');
        }
    })();

    return `${protocol}//${host}${path}`;
};

export const getOAuthAuthorizationUrl = ({
    provider,
    scope,
    config,
    selectAccountDisabled,
}: {
    provider: ImportProvider | OAUTH_PROVIDER;
    scope: string;
    config: ApiEnvironmentConfig;
    selectAccountDisabled: boolean;
}) => {
    const params = new URLSearchParams();

    params.append('redirect_uri', getOAuthRedirectURL(provider));
    params.append('response_type', 'code');
    params.append('scope', scope);

    if (provider === ImportProvider.OUTLOOK || provider === OAUTH_PROVIDER.OUTLOOK) {
        // Microsoft doesn't support combining prompt values (e.g. "select_account consent").
        // The feature flag for the consent parameter was removed, thus now the 'account_selection' parameter takes precedence over 'consent'.
        if (!selectAccountDisabled) {
            params.append('prompt', 'select_account');
        } else {
            params.append('prompt', 'consent');
        }

        return generateOutlookOAuthUrl(params, config);
    }

    if (provider === OAUTH_PROVIDER.ZOOM) {
        return generateZoomOAuthUrl(params, config);
    }

    throw new Error('Provider does not exist');
};

export const getProviderNumber = (provider: ImportProvider | OAUTH_PROVIDER) => {
    switch (provider) {
        case ImportProvider.GOOGLE:
        case OAUTH_PROVIDER.GOOGLE:
            return OAUTH_PROVIDER.GOOGLE;
        case ImportProvider.OUTLOOK:
        case OAUTH_PROVIDER.OUTLOOK:
            return OAUTH_PROVIDER.OUTLOOK;
        case OAUTH_PROVIDER.ZOOM:
            return OAUTH_PROVIDER.ZOOM;
        case OAUTH_PROVIDER.GSUITE:
            return OAUTH_PROVIDER.GSUITE;
        case OAUTH_PROVIDER.MICROSOFT_BUSINESS:
            return OAUTH_PROVIDER.MICROSOFT_BUSINESS;
        default:
            throw new Error('Provider does not exist');
    }
};
